import { FormControl, ValidatorFn } from '@angular/forms';
import { FileFormArray } from './file-form-array';
import { isEmpty, includes } from 'lodash';

export class FileNameControl extends FormControl {
    private static readonly invalidFileExtensionError = 'invalidFileExtension';
    private static readonly duplicateFileNameError = 'duplicateFileName';
    private static readonly invalidFileSizeError = 'invalidFileSize';

    constructor(
        private readonly fileName: string,
        private readonly fileSize: number,
        private allowedFileExtensions: string[],
        private maxFileSize: number,
        private readonly fileFormArray: FileFormArray,
        private readonly excludeFileNames: string[]
    ) {
        super(fileName, [
            FileNameControl.invalidFileExtensionValidator(fileName, allowedFileExtensions),
            FileNameControl.duplidateFileValidator(fileFormArray, excludeFileNames),
            FileNameControl.invalidFileSizeValidator(fileSize, maxFileSize)
        ]);
    }

    public get hasInvalidFileExtension(): boolean {
        return this.hasError(FileNameControl.invalidFileExtensionError);
    }

    public get invalidFileExtensionError(): string {
        return this.getError(FileNameControl.invalidFileExtensionError);
    }

    public get hasInvalidFileSize(): boolean {
        return this.hasError(FileNameControl.invalidFileSizeError);
    }

    public get invalidFileSizeError(): string {
        return this.getError(FileNameControl.invalidFileSizeError);
    }

    public get hasDuplidateFileError(): boolean {
        return this.hasError(FileNameControl.duplicateFileNameError);
    }

    public updateAllowedFileExtensions(allowedFileExtensions: string[]) {
        this.allowedFileExtensions = allowedFileExtensions;

        super.setValidators([
            FileNameControl.invalidFileExtensionValidator(this.fileName, this.allowedFileExtensions),
            FileNameControl.duplidateFileValidator(this.fileFormArray, this.excludeFileNames),
            FileNameControl.invalidFileSizeValidator(this.fileSize, this.maxFileSize)
        ]);
        
        super.updateValueAndValidity();
    }

    private static invalidFileExtensionValidator(fileName: string, allowedFileExtensions: string[]): ValidatorFn {
        return () => {
            if (isEmpty(allowedFileExtensions)) {
                return null;
            }

            const extension = this.getFileExtension(fileName);

            return allowedFileExtensions.indexOf(extension) === -1
                ? { [this.invalidFileExtensionError]: `Invalid file extension: ${extension}` }
                : null;
        };
    }

    private static invalidFileSizeValidator(fileSize: number, maxFileSize: number): ValidatorFn {
        return () => {
            if (fileSize > maxFileSize) {
                return { [this.invalidFileSizeError]: `invalid file size, max size is: ${(maxFileSize / 1024 / 1024).toFixed(1)}Mb` };
            }

            return null;
        };
    }

    private static duplidateFileValidator(fileFormArray: FileFormArray, excludeFileNames: string[]): ValidatorFn {
        return (currentControl: FileNameControl) => {
            const invalidResult = { [this.duplicateFileNameError]: true };

            for (let group of fileFormArray.fileFormGroups) {
                if (!isEmpty(excludeFileNames) && includes(excludeFileNames, currentControl.value)) {
                    return invalidResult;
                }

                if (currentControl === group.fileNameControl) {
                    continue;
                }

                if (group.fileNameControl.value === currentControl.value) {
                    return invalidResult;
                }
            }

            return null;
        };
    }

    private static getFileExtension(fileName: string): string {
        const i = fileName.lastIndexOf('.');

        if (i === -1) {
            return null;
        }

        return fileName.substring(i).toLowerCase();
    }      
}